# Migrating SEEK Application Export

The retired SEEK Application Export API allowed a partner to retrieve candidate applications for a hirer, including associated documents and screening question responses.
It was also known as “Optimised Apply on SEEK” or simply “SAE”.

SAE had a number of shortcomings:

- It required partners to explicitly poll for new applications on a hirer-by-hirer basis.
  For partners with a large number of hirers this lead to wasted resources for both the partner and SEEK.

- It had limited extensibility for passing richer SEEK Profile information, document types, and application questionnaire responses.

- Its design had little in common with the retired Job Posting API or the new SEEK API.
  This required duplicated integration effort from partners.

The following documentation compares SAE with the SEEK API for application export.
For more general information, see the [Optimised Apply use case documentation].

[Optimised Apply use case documentation]: /use-cases/application-export#retrieving-candidate-applications

## Event delivery

The SEEK API replaces per-hirer polling with a new [event] mechanism.
A [`CandidateApplicationCreated` event] is emitted whenever a candidate submits an application on SEEK.
Your software can trigger an export of said application from this event.

`CandidateApplicationCreated` events can be consumed in three ways:

1. Creating a partner-scoped [webhook] subscription that will receive candidate applications for all of your SEEK hirers.
   This is the recommended option if it fits with your software architecture.

2. Creating a separate webhook subscription for each SEEK hirer using Optimised Apply.

   This replicates the per-hirer event delivery of SAE.
   However, it requires that you create the appropriate webhook subscription as part of onboarding a new hirer.

3. [Polling] an event stream for candidate applications across all your SEEK hirers.
   This is a fallback for partners that don’t have the capability to receive webhooks.

   You should avoid this option if at all possible;
   the SEEK API only permits infrequent polling which will degrade the experience for your hirers.
   See [“why webhooks”] for an explanation of why frequent polling is harmful.

SEEK will only send `CandidateApplicationCreated` events that relate to the elected hirers,
which makes it easier to monitor the status of your pilot and track down potential issues.

Before you scale your build to all of your SEEK hirers, we recommend switching over to a partner-scoped webhook subscription to reduce per-request overhead and avoid additional steps when onboarding or offboarding a hirer.
You should create the partner-scoped webhook subscription before deleting your existing webhook subscriptions so that your software does not miss any events in between.

[“why webhooks”]: /events#why-webhooks
[event]: /events
[webhook]: /events/webhooks
[polling]: /events/polling#event-delivery-flow
[`candidateapplicationcreated` event]: /use-cases/application-export#retrieving-candidate-applications

## Data structure

The SEEK API replaces SAE’s JSON and XML responses with a GraphQL schema based on [HR Open Standards].
For most fields there’s a direct mapping from the previous JSON structure to GraphQL.

You could use SAE’s `test.api.seek.com.au` to compare data structures with the SEEK API’s [Playground] environment.
They both point to the same internal mock data but use different identifiers:

| SAE application ID | Advertiser ID | SEEK API candidate application profile                            |
| :----------------: | :-----------: | ----------------------------------------------------------------- |
|    `800000000`     |    `10000`    | `seekAnzPublicTest:candidateProfile:apply:7DtW6Q68gk2R4okNGhvg1Y` |
|    `800000001`     |    `10000`    | `seekAnzPublicTest:candidateProfile:apply:4QM5fWQbdekL9gPtPZrzex` |
|    `800000003`     |    `10001`    | `seekAnzPublicTest:candidateProfile:apply:8LKX1QrkJk5JuU2AfW6tsc` |
|    `800000004`     |    `10001`    | `seekAnzPublicTest:candidateProfile:apply:UHeZTe27g1ZJZoHMiwzmPJ` |

The following annotated examples compare SAE application ID `800000001` against the SEEK API.

[hr open standards]: https://www.hropenstandards.org/

### Comparison

This table compares the data structures of a candidate application in SAE with the SEEK API [`CandidateProfile`] object.

import { IconTick } from 'braid-design-system';

| SAE field                       |  Deprecated  | [`CandidateProfile`] field                                                                                                                                                                                                  |
| :------------------------------ | :----------: | :-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
| `ID`                            | <IconTick /> | <p>SEEK no longer assigns sequential IDs to candidate applications.</p><ul><li><p>Use `profileId.value` to uniquely identify an application.</p></li><li><p>Use the opaque `cursor` string for event polling.</p></li></ul> |
| `CandidateApplicationProfileID` |              | <p>`profileId.value`</p><p>This is a SEEK API migration feature enabled by the `&include=ObjectIdentifiers` query parameter.</p>                                                                                            |
| `DateSubmitted`                 | <IconTick /> | Use `createDateTime` for an RFC 3339 equivalent.                                                                                                                                                                            |
| `JobReference`                  |              | `associatedPositionProfile.seekHirerJobReference`                                                                                                                                                                           |
| `Email`                         |              | `person.communication.email.address`                                                                                                                                                                                        |
| `FirstName`                     |              | `person.name.given`                                                                                                                                                                                                         |
| `LastName`                      |              | `person.name.family`                                                                                                                                                                                                        |
| `JobSeekerID`                   | <IconTick /> | Use `candidate.documentId.value` to uniquely identify an individual candidate across multiple candidate applications.                                                                                                       |
| `Phone`                         |              | `person.communication.phone.formattedNumber`                                                                                                                                                                                |
| `PrimaryJobSeekingActivity`     | <IconTick /> | Use the most recent entry in `employment`.                                                                                                                                                                                  |
| &emsp;`.IsNewToWorkForce`       | <IconTick /> | Candidates that are new to the workforce will have an empty `employment`.                                                                                                                                                   |
| &emsp;`.CompanyName`            |              | `employment[].organization`                                                                                                                                                                                                 |
| &emsp;`.JobTitle`               |              | `employment[].positionHistories[].title`                                                                                                                                                                                    |
| &emsp;`.TimeInRole`             | <IconTick /> | Use the number of months between `employment[].positionHistories[].start` and `.end`.                                                                                                                                       |
| `Attachments`                   |              | `attachments`                                                                                                                                                                                                               |
| &emsp;`.Href`                   |              | `attachments[].url`                                                                                                                                                                                                         |
| &emsp;`.Type`                   |              | `attachments[].seekRoleCodeCode`                                                                                                                                                                                            |
| &emsp;`.DateUploaded`           | <IconTick /> | <p>File metadata is not available over GraphQL.</p><p>The SEEK API supports efficient `HEAD` requests to retrieve the same information over HTTP.</p>                                                                       |
| &emsp;`.Size`                   | <IconTick /> | <p>File metadata is not available over GraphQL.</p><p>The SEEK API supports efficient `HEAD` requests to retrieve the same information over HTTP.</p>                                                                       |
| &emsp;`.HasVirus`               | <IconTick /> | The SEEK API will automatically filter out attachments with viruses.                                                                                                                                                        |
| &emsp;`.Hash`                   | <IconTick /> | Hashes have been removed from both SAE and the SEEK API.                                                                                                                                                                    |

[`candidateprofile`]: /schema/named-type/CandidateProfile

### Querying SAE

This retrieves application ID `800000001` from `test.api.seek.com`:

```http label="Request"
GET /v1/advertiser/applications?afterId=800000000&beforeId=800000002&include=ObjectIdentifiers,PrimaryJobSeekingInfo
Host: test.api.seek.com.au
```

```jsonc label="Response (JSON)"
{
  "JobApplications": {
    // => `associatedPositionProfile.positionOrganizations[].seekAnzAdvertiserId`
    "AdvertiserID": 10000,
    "AfterDate": null,
    "AfterID": "800000000",
    "AgentID": null,
    "BeforeDate": null,
    "BeforeID": "800000002",
    // => `associatedPositionProfile.positionOrganizations[].id.value`
    // This is a migration feature enabled by the `&include=ObjectIdentifiers` query parameter.
    "HirerID": "seekAnzPublicTest:organization:seek:93WyyF1h",
    "JobApplicationsList": [
      {
        // SEEK no longer assigns sequential IDs to candidate applications.
        // - Use `profileId.value` to uniquely identify an application.
        // - Use the opaque `cursor` string for event polling.
        "ID": 800000001,

        // => `profileId.value`
        // This is a migration feature enabled by the `&include=ObjectIdentifiers` query parameter.
        "CandidateApplicationProfileID": "seekAnzPublicTest:candidateProfile:apply:4QM5fWQbdekL9gPtPZrzex",

        // => Use `createDateTime` for an RFC 3339 equivalent.
        "DateSubmitted": "/Date(1567974732479+1000)/",

        // => `associatedPositionProfile.seekHirerJobReference`
        "JobReference": "REQ-65558",

        // => `person.communication.email[].address`
        "Email": "Amelia.Collins29@example.net",

        // => `person.name.given`
        "FirstName": "Amelia",

        // => `person.name.family`
        "LastName": "Collins",

        // Use `candidate.documentId.value` to uniquely identify an individual
        // candidate across multiple candidate applications.
        "JobSeekerID": "8001",

        // => `person.communication.phone.formattedNumber`
        "Phone": "0491 570 158",

        // Use the most recent entry in `employment`.
        "PrimaryJobSeekingActivity": {
          // Candidates that are new to the workforce will have an empty `employment`.
          "IsNewToWorkForce": false,

          // => `employment[].organization`
          "CompanyName": "Mason Corp",

          // => `employment[].positionHistories[].title`
          "JobTitle": "Quality Assurance Specialist",

          // Use the number of months between `employment[].positionHistories[].start` and `.end`.
          "TimeInRole": 20
        },

        "Attachments": [
          {
            // => `attachments[].url`
            "Href": "advertiser/applications/coverLetter/800000001",

            // => `attachments[].seekRoleCode`
            "Type": "coverLetter",

            // File metadata is not available over GraphQL.
            // The SEEK API supports efficient `HEAD` requests to retrieve the same
            // information over HTTP.
            "DateUploaded": "/Date(1569328951485+1000)/",
            "Size": 33,

            // The SEEK API will automatically filter out attachments with viruses.
            "HasVirus": null,

            // Hashes have been removed from both SAE and the SEEK API.
            "Hash": "N/A"
          },
          {
            "DateUploaded": "/Date(1569328951485+1000)/",
            "HasVirus": null,
            "Hash": "N/A",
            "Href": "advertiser/applications/resume/800000001",
            "Size": 11789,
            "Type": "resume"
          }
        ]
      }
    ]
  }
}
```

```xml label="Response (XML)"
<?xml version="1.0" encoding="utf-8"?>
<seek xsi:noNamespaceSchemaLocation="https://websk.seekprod.local:9302/schemas/jobApplications" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
    <jobApplications advertiserId="10000" hirerId="seekAnzPublicTest:organization:seek:93WyyF1h" afterId="800000000" beforeId="800000002">
        <jobApplication id="800000001" candidateApplicationProfileId="seekAnzPublicTest:candidateProfile:apply:4QM5fWQbdekL9gPtPZrzex" jobSeekerId="8001" jobReference="REQ-65558" firstName="Amelia" lastName="Collins" phone="0491 570 158" email="Amelia.Collins29@example.net" dateSubmitted="2019-09-09T06:32:12.479">
            <attachment type="resume" href="advertiser/applications/resume/800000001" dateUploaded="2019-09-08T20:32:13.944" size="11789" hash="N/A"/>
            <attachment type="coverLetter" href="advertiser/applications/coverLetter/800000001" dateUploaded="2019-09-08T20:32:13.944" size="33" hash="N/A"/>
            <primaryJobSeekingInfo>
                <jobTitle>Quality Assurance Specialist</jobTitle>
                <companyName>Mason Corp</companyName>
                <timeInRole>20</timeInRole>
                <isNewToWorkForce>false</isNewToWorkForce>
            </primaryJobSeekingInfo>
        </jobApplication>
    </jobApplications>
</seek>
```

### Querying the SEEK API

This is an example query to illustrate how SAE’s fields are represented in the SEEK API.
You can remove unneeded fields or select new SEEK API fields based on your requirements.

```graphql lovato-ignore-no-deprecated-schema
query ($id: String!) {
  candidateProfile(id: $id) {
    profileId {
      value
    }
    createDateTime
    associatedPositionProfile {
      profileId {
        value
      }
      seekHirerJobReference
      positionOrganizations {
        id {
          value
        }
        seekAnzAdvertiserId
      }
    }
    candidate {
      documentId {
        value
      }
      person {
        name {
          given
          family
        }
        communication {
          phone {
            formattedNumber
          }
          email {
            address
          }
        }
      }
    }
    employment {
      organization {
        name
      }
      positionHistories {
        start
        end
        title
      }
    }
    attachments {
      seekRoleCode
      url
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:candidateProfile:apply:4QM5fWQbdekL9gPtPZrzex"
}
```

```jsonc
{
  "candidateProfile": {
    "profileId": {
      "value": "seekAnzPublicTest:candidateProfile:apply:4QM5fWQbdekL9gPtPZrzex"
    },
    "createDateTime": "2019-09-08T20:32:12.479Z",
    "associatedPositionProfile": {
      "profileId": {
        "value": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
      },
      "seekHirerJobReference": "REQ-65558",
      "positionOrganizations": [
        {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
          },
          // This corresponds to SAE's advertiser ID.
          //
          // See the hirer identifier guide for a discussion of
          // SEEK advertiser IDs versus hirer IDs.
          "seekAnzAdvertiserId": 10000
        }
      ]
    },
    "candidate": {
      "documentId": {
        "value": "seekAnzPublicTest:candidate:feed:5PGXAHysjZdkQYwZghfL4bRCqvZ7ZM"
      },
      "person": {
        "name": {
          "given": "Amelia",
          "family": "Collins"
        },
        "communication": {
          "phone": [
            {
              "formattedNumber": "0491 570 158"
            }
          ],
          "email": [
            {
              "address": "Amelia.Collins29@example.net"
            }
          ]
        }
      }
    },
    "employment": [
      {
        "organization": {
          "name": "Mason Corp"
        },
        "positionHistories": [
          {
            // SAE's `TimeInRole` is calculated as 20 months between these dates
            "start": "2017-05",
            "end": "2018-12",

            "title": "Quality Assurance Specialist"
          }
        ]
      }
    ],
    "attachments": [
      {
        "seekRoleCode": "CoverLetter",
        "url": "https://graphql.seek.com/anzPublicTest/applications/4QM5fWQbdekL9gPtPZrzex/attachments/7xF8zuiaFdnFo84DBSZVEjiu9EoWZasQRAr5YSYeHvGj"
      },
      {
        "seekRoleCode": "Resume",
        "url": "https://graphql.seek.com/anzPublicTest/applications/4QM5fWQbdekL9gPtPZrzex/attachments/7wS9uaWkwNrz4L5EUjNoBpVHCo61gaTSTdqiEeqxPVAk"
      }
    ]
  }
}
```

[playground]: /graphql/playground

## Transitioning existing hirers

The SEEK API and SAE can be safely run in parallel for the same hirer.
Each candidate application will appear in both integrations but with a different identifier and timestamp.
This requires care to avoid duplicate candidate applications appearing in your software.

SEEK supports three methods to transition existing hirers from SAE to the SEEK API.
You can choose the method that best fits your software’s architecture.

### Method 1: Deduplicating applications

```mermaid Deduplicating applications
flowchart TD
  sae(SAE) --Candidate application--> duplicateid{Seen application ID?}
  seekapi(SEEK API) --Candidate application--> duplicateid

  duplicateid --Yes--> merge([Update/ignore])
  duplicateid --No--> import[(Store candidate application)]
```

SAE could return candidate application profile IDs in addition to its legacy identifiers.
Your software could opt-in to this by adding `&include=ObjectIdentifiers,PrimaryJobSeekingInfo` to your SAE request URLs.

You can export candidate applications from both integrations.
If you receive a candidate application profile ID that has already been exported,
you can either retain the latest copy or filter it out if your software does not support updates.
Once you validate your SEEK API integration is working correctly you can safely disable your SAE integration.

This is the recommended method if it’s feasible to modify your existing SAE integration.

### Method 2: Filtering by job ad

```mermaid Filtering by job ad
flowchart TD
  sae(SAE) --Candidate application--> existingad{Old job ad?}
  seekapi(SEEK API) --Candidate application--> newad{New job ad?}

  existingad --No--> ignore1([Ignore])
  newad --No--> ignore2([Ignore])

  existingad --Yes--> import[(Store candidate application)]
  newad --Yes--> import[(Store candidate application)]
```

You could continue to use SAE for existing job ads while starting to use the SEEK API for newly posted ads.
Your software will need to filter candidate applications based on their associated position to ensure they came from the intended integration.

This is the recommended method if your software supports multiple applications from the same candidate to the same position.
However, this requires running your SAE integration until the hirer’s existing job ads have closed.

### Method 3: Deduplicating candidates

```mermaid Deduplicating candidates
flowchart TD
  sae(SAE) --Candidate application--> duplicateemail{Seen email address?}
  seekapi(SEEK API) --Candidate application--> duplicateemail

  duplicateemail --Yes--> merge([Update/ignore])
  duplicateemail --No--> import[(Store candidate application)]
```

You can export candidate applications from both integrations and deduplicate based on the candidate’s email address.
SEEK requires candidates to sign in with their email address before using SEEK’s Apply Form.
This allows the candidate’s email address to uniquely identify them across both integrations.

While SEEK doesn’t prevent multiple applications from the same candidate to the same position, this is rare in practice.
If your software is already deduplicating candidates this is the simplest method.

## Common problems

### Serving attachment downloads

Attachments may be downloaded [one of two ways]:

- At the time of candidate application.

  In this scenario,
  your software retrieves the application and its related attachments upfront,
  and does not need to make any subsequent calls to SEEK.
  This allows you to cleanly cut over from SAE to the SEEK API without any disruption to existing application data.

- On demand.

  In this scenario,
  your software keeps track of the attachment download URLs associated with each application.
  This means that it will have to store a different URL based on whether the application was retrieved from SAE or the SEEK API,
  and be able to download attachments from either API, including using the appropriate credentials.

  Support for SAE attachment downloads may be dropped after a reasonable period of time.
  An attachment will remain for 180 days after the [close date] of its associated job ad.

[one of two ways]: /use-cases/application-export/attachments#downloading-attachment-files
[close date]: /use-cases/job-posting/key-concepts#job-ad-lifecycle

### Integer identifier continuity

We recommend that you use our new string object identifiers as they provide [important benefits](/graphql/seek-api-conventions#object-identifiers).
However, if it’s not immediately feasible to switch your software over to a more expansive data type, you can generate your own backward-compatible identifiers.
This gives you more control in selecting the appropriate data type for now and also evolving your software in future.

Database systems often support collision-safe number generation.
The following MySQL example generates a numeric identifier using an AUTO_INCREMENT column in place of the old SAE application ID,
and also applies a unique constraint on the new object identifier for deduplication.

```sql
-- Simplified representation of an existing table in your database
CREATE TABLE candidate_application (
  old_numeric_id INT  NOT NULL,
  new_object_id  TEXT NOT NULL,

  PRIMARY KEY (old_numeric_id),
  UNIQUE KEY  (new_object_id)
);

-- A new table for generating your own incremental identifiers
CREATE TABLE candidate_application_id_generator (
  id INT NOT NULL AUTO_INCREMENT,

  PRIMARY KEY (id)
);
​
-- Start your own identifiers at a reasonable jump from existing SAE identifiers
ALTER TABLE
  candidate_application_id_generator AUTO_INCREMENT = 123;
​
-- For SEEK API applications, generate and insert a numeric identifier
-- This is rudimentary and will throw an error on a duplicate object identifier
INSERT INTO candidate_application_id_generator (id) VALUES (DEFAULT);
INSERT INTO candidate_application (old_numeric_id, new_object_id)
VALUES
  (
    LAST_INSERT_ID(), -- This is safe even with concurrent connections
    'seekAnzPublicTest:candidateProfile:apply:7DtW6Q68gk2R4okNGhvg1Y'
  );​
```
